import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import customerService from '../../services/customerService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';

const PLATFORM_COLORS = {
  web: { bg: 'rgba(16,185,129,0.12)', color: '#059669' },
  ios: { bg: 'rgba(99,102,241,0.12)', color: '#4338ca' },
  android: { bg: 'rgba(34,197,94,0.12)', color: '#16a34a' },
  mobile: { bg: 'rgba(99,102,241,0.12)', color: '#4338ca' },
};

const mobileBadgeLabel = (lastPlatform, isRtl) => {
  if (lastPlatform === 'ios') return isRtl ? 'آيفون (iOS)' : 'iPhone (iOS)';
  if (lastPlatform === 'android') return isRtl ? 'أندرويد' : 'Android';
  return isRtl ? 'تطبيق الجوال' : 'Mobile App';
};

const SOURCE_COLORS = {
  0: PLATFORM_COLORS.mobile, // App
  1: PLATFORM_COLORS.web,    // Web
};

const StatTile = ({ number, label }) => (
  <div className="stat-card">
    <div className="stat-card-content">
      <div className="stat-details">
        <div className="stat-number">{number}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  </div>
);

const formatDateTime = (value, isRtl) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(isRtl ? 'ar-SA' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDate = (value, isRtl) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(isRtl ? 'ar-SA' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getSourceLabel = (source, isRtl) => {
  const labels = {
    0: isRtl ? 'التطبيق' : 'App',
    1: isRtl ? 'الموقع' : 'Web',
  };
  return labels[source] ?? (isRtl ? 'غير معروف' : 'Unknown');
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isRtl } = useLocalization();

  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setIsLoading(true);
    setError(null);
    customerService.getById(id)
      .then((data) => setCustomer(data))
      .catch((err) => setError(err.response?.data?.message || err.message || (isRtl ? 'تعذر تحميل البيانات' : 'Failed to load data')))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isLoading) return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading...'} />;

  if (error || !customer) {
    return (
      <div className="page-content">
        <div className="error-alert"><span>{error || (isRtl ? 'العميل غير موجود' : 'Customer not found')}</span></div>
        <Button variant="secondary" onClick={() => navigate('/customers')}>{isRtl ? '← العودة' : '← Back'}</Button>
      </div>
    );
  }

  const avatarSrc = getProductImageUrl(customer.avatarUrl);
  const displayName = customer.fullName || (isRtl ? 'بدون اسم' : 'Unnamed customer');
  const neitherPlatform = !customer.usesWeb && !customer.usesMobile;

  return (
    <div className="page-content customer-detail-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <Button variant="secondary" onClick={() => navigate('/customers')}>{isRtl ? '← العودة' : '← Back'}</Button>
            <div>
              <h1 className="page-title">{displayName}</h1>
              <p className="page-description"><span className="ltr-text">{customer.phoneE164}</span></p>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-grid mb-6">
        <StatTile number={customer.ordersCount} label={isRtl ? 'الطلبات' : 'Orders'} />
        <StatTile number={`${customer.totalSpent?.toLocaleString?.() ?? customer.totalSpent} ${isRtl ? 'د.ع' : 'IQD'}`} label={isRtl ? 'إجمالي الإنفاق' : 'Total Spent'} />
        <StatTile number={formatDate(customer.lastOrderAtUtc, isRtl)} label={isRtl ? 'آخر طلب' : 'Last Order'} />
        <StatTile number={formatDate(customer.createdAt, isRtl)} label={isRtl ? 'تاريخ التسجيل' : 'Registered'} />
      </div>

      <div className="dashboard-grid">
        {/* Customer info card */}
        <div className="card card-full-width">
          <div className="card-header"><h3 className="card-title">{isRtl ? 'تفاصيل العميل' : 'Customer Details'}</h3></div>
          <div className="card-body customer-detail-body">
            <div className="customer-detail-avatar">
              {avatarSrc ? <img src={avatarSrc} alt="" /> : <div className="customer-detail-avatar-placeholder">{displayName.charAt(0).toUpperCase()}</div>}
            </div>
            <div className="promo-detail-fields">
              <div className="promo-detail-row"><span>{isRtl ? 'الاسم الكامل' : 'Full Name'}</span><span>{customer.fullName || '—'}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'رقم الهاتف' : 'Phone'}</span><span className="ltr-text">{customer.phoneE164}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'المدينة' : 'City'}</span><span>{customer.city || '—'}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'العنوان' : 'Address'}</span><span>{customer.address || '—'}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'التحقق' : 'Verified'}</span><span className={`badge ${customer.isVerified ? 'badge-success' : 'badge-neutral'}`}>{customer.isVerified ? (isRtl ? 'موثّق' : 'Verified') : (isRtl ? 'غير موثّق' : 'Unverified')}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'تاريخ التسجيل' : 'Registered On'}</span><span>{formatDateTime(customer.createdAt, isRtl)}</span></div>
              {customer.note && (
                <div className="promo-detail-row"><span>{isRtl ? 'ملاحظات' : 'Notes'}</span><span>{customer.note}</span></div>
              )}
            </div>
          </div>
        </div>

        {/* Platform usage card */}
        <div className="card">
          <div className="card-header"><h3 className="card-title">{isRtl ? 'المنصة المستخدمة' : 'Platform Used'}</h3></div>
          <div className="card-body">
            {neitherPlatform ? (
              <p className="empty-description">{isRtl ? 'لا يوجد نشاط بعد على الموقع أو التطبيق' : 'No activity on the website or app yet'}</p>
            ) : (
              <div className="platform-badges-row">
                {customer.usesWeb && (
                  <span className="badge platform-badge" style={{ background: PLATFORM_COLORS.web.bg, color: PLATFORM_COLORS.web.color }}>
                    {isRtl ? 'الموقع الإلكتروني' : 'Website'}
                  </span>
                )}
                {customer.usesMobile && (
                  <span className="badge platform-badge" style={{ background: (PLATFORM_COLORS[customer.lastPlatform] || PLATFORM_COLORS.mobile).bg, color: (PLATFORM_COLORS[customer.lastPlatform] || PLATFORM_COLORS.mobile).color }}>
                    {mobileBadgeLabel(customer.lastPlatform, isRtl)}
                  </span>
                )}
              </div>
            )}
            <div className="promo-detail-row" style={{ marginTop: 'var(--sp-3)' }}>
              <span>{isRtl ? 'آخر تسجيل دخول' : 'Last Login'}</span>
              <span>{formatDateTime(customer.lastLoginAtUtc, isRtl)}</span>
            </div>
            {customer.mobileDevicePlatforms?.length > 0 && (
              <div className="promo-detail-row">
                <span>{isRtl ? 'أجهزة الإشعارات' : 'Notification Devices'}</span>
                <span>{customer.mobileDevicePlatforms.join(', ')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Recent orders */}
        <div className="card card-full-width">
          <div className="card-header"><h3 className="card-title">{isRtl ? 'أحدث الطلبات' : 'Recent Orders'}</h3></div>
          <div className="card-body p-0">
            {(customer.recentOrders || []).length === 0 ? (
              <div className="empty-state"><p className="empty-description">{isRtl ? 'لا توجد طلبات' : 'No orders yet'}</p></div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{isRtl ? 'رقم الطلب' : 'Order #'}</th>
                      <th>{isRtl ? 'الإجمالي' : 'Total'}</th>
                      <th>{isRtl ? 'الحالة' : 'Status'}</th>
                      <th>{isRtl ? 'المصدر' : 'Source'}</th>
                      <th>{isRtl ? 'التاريخ' : 'Date'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customer.recentOrders.map((o) => (
                      <tr key={o.id} className="order-row" onClick={() => navigate(`/orders/${o.id}`)} style={{ cursor: 'pointer' }}>
                        <td><span className="order-number-link">{o.orderNumber}</span></td>
                        <td>{o.total?.toLocaleString?.() ?? o.total} {isRtl ? 'د.ع' : 'IQD'}</td>
                        <td>{isRtl ? o.statusAr : o.statusEn}</td>
                        <td>
                          <span className="badge" style={{ background: (SOURCE_COLORS[o.source] ?? SOURCE_COLORS[0]).bg, color: (SOURCE_COLORS[o.source] ?? SOURCE_COLORS[0]).color, fontWeight: 600 }}>
                            {getSourceLabel(o.source, isRtl)}
                          </span>
                        </td>
                        <td>{formatDate(o.createdAtUtc, isRtl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .customer-detail-body { display: flex; gap: var(--sp-6); flex-wrap: wrap; }
        .customer-detail-avatar {
          width: 96px; height: 96px; flex-shrink: 0;
          border-radius: var(--r-full); overflow: hidden; background: var(--neutral-100);
          display: flex; align-items: center; justify-content: center;
        }
        .customer-detail-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .customer-detail-avatar-placeholder {
          width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
          font-size: 2rem; font-weight: 700; color: var(--primary); background: rgba(30,58,95,0.08);
        }
        .promo-detail-fields { flex: 1; min-width: 260px; display: flex; flex-direction: column; gap: var(--sp-2); }
        .promo-detail-row {
          display: flex; justify-content: space-between; gap: var(--sp-4);
          padding: var(--sp-2) 0; border-bottom: 1px solid var(--border-light);
          font-size: var(--fs-sm);
        }
        .promo-detail-row span:first-child { color: var(--text-muted); }
        .promo-detail-row span:last-child { color: var(--text); font-weight: 500; text-align: end; }
        .platform-badges-row { display: flex; gap: var(--sp-2); flex-wrap: wrap; }
        .platform-badge { font-weight: 600; padding: var(--sp-2) var(--sp-3); font-size: var(--fs-sm); }
        .order-row:hover { background: var(--surface-2); }
      `}</style>
    </div>
  );
};

export default CustomerDetail;
